/**
 * Helpers for the time-of-day cutoff schedules (booking and cancellation).
 *
 * The backend returns the FIRST range covering the class's IST start time, and
 * ranges are half-open — [start, end). Two consequences drive everything here:
 * a range must end exactly where the next one starts (07:00, not 06:59), and a
 * wide range above a narrow one makes the narrow one unreachable. Neither shows
 * up as an error; uncovered times fall back to the studio's flat value.
 */

export interface TimeRange {
  startTime: string;
  endTime: string;
  cutoffHours: number;
}

const MINUTES_IN_DAY = 24 * 60;
const SLICE_MINUTES = 60;

/** "HH:MM" → minutes past midnight, or null if malformed. "24:00" is valid. */
export function timeToMinutes(value: string): number | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours < 0 || hours > 24 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function minutesToTime(mins: number): string {
  const clamped = Math.max(0, Math.min(MINUTES_IN_DAY, Math.round(mins)));
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

/** Length of a range in minutes, counting a wrap past midnight. */
function spanOf(range: TimeRange): number {
  const start = timeToMinutes(range.startTime);
  const end = timeToMinutes(range.endTime);
  if (start == null || end == null) return 0;
  return (end - start + MINUTES_IN_DAY) % MINUTES_IN_DAY || MINUTES_IN_DAY;
}

/**
 * Add a range, leaving the schedule covering the full 24 hours.
 *
 * The new range takes whatever is left: it starts where the last one ended and
 * runs back round to where the first one begins. When nothing is left it
 * divides the widest range instead — the widest always has room, so repeated
 * adds keep producing usable rows rather than shrinking slivers.
 *
 * Returns the new list, since dividing also modifies an existing range.
 */
export function appendRange(ranges: TimeRange[], defaultCutoffHours: number): TimeRange[] {
  if (!ranges.length) {
    return [{ startTime: '00:00', endTime: '24:00', cutoffHours: defaultCutoffHours }];
  }

  const lastEnd = timeToMinutes(ranges[ranges.length - 1].endTime);
  const firstStart = timeToMinutes(ranges[0].startTime);
  if (lastEnd == null || firstStart == null) {
    return [...ranges, { startTime: '06:00', endTime: '12:00', cutoffHours: defaultCutoffHours }];
  }

  if (lastEnd % MINUTES_IN_DAY !== firstStart % MINUTES_IN_DAY) {
    return [
      ...ranges,
      {
        startTime: minutesToTime(lastEnd),
        endTime: minutesToTime(firstStart),
        cutoffHours: defaultCutoffHours,
      },
    ];
  }

  let widest = 0;
  ranges.forEach((range, i) => {
    if (spanOf(range) > spanOf(ranges[widest])) widest = i;
  });
  if (spanOf(ranges[widest]) < 2 * SLICE_MINUTES) return ranges;

  const target = ranges[widest];
  const seam = (timeToMinutes(target.endTime)! - SLICE_MINUTES + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  return [
    ...ranges.slice(0, widest),
    { ...target, endTime: minutesToTime(seam) },
    { startTime: minutesToTime(seam), endTime: target.endTime, cutoffHours: defaultCutoffHours },
    ...ranges.slice(widest + 1),
  ];
}

/**
 * Apply one field edit, keeping neighbours joined: moving a range's end moves
 * the next range's start, and moving its start moves the previous range's end.
 * Editing `cutoffHours` moves nothing.
 */
export function knitRanges(
  ranges: TimeRange[],
  index: number,
  field: 'startTime' | 'endTime' | 'cutoffHours',
  value: string | number
): TimeRange[] {
  const next = ranges.map((r, i) => (i === index ? { ...r, [field]: value } : r));
  if (field === 'endTime' && index + 1 < next.length) {
    next[index + 1] = { ...next[index + 1], startTime: String(value) };
  }
  if (field === 'startTime' && index > 0) {
    next[index - 1] = { ...next[index - 1], endTime: String(value) };
  }
  return next;
}

/** The backend's first-match rule, so the editor can predict its result. */
function firstMatchIndex(ranges: TimeRange[], minutesOfDay: number): number | null {
  for (let i = 0; i < ranges.length; i++) {
    const start = timeToMinutes(ranges[i].startTime);
    const end = timeToMinutes(ranges[i].endTime);
    if (start == null || end == null) continue;
    if (typeof ranges[i].cutoffHours !== 'number') continue;
    const matches =
      start <= end
        ? minutesOfDay >= start && minutesOfDay < end
        : minutesOfDay >= start || minutesOfDay < end;
    if (matches) return i;
  }
  return null;
}

export interface RangeAudit {
  /** Ranges an earlier range already covers, so they can never match. */
  unreachable: number[];
  /** Uncovered spans as "HH:MM–HH:MM"; one crossing midnight is reported once. */
  gaps: string[];
}

/**
 * Check every minute of the day against the backend's matching rule and report
 * what the list of rows does not show: ranges that never match, and times no
 * range covers.
 */
export function auditRanges(ranges: TimeRange[]): RangeAudit {
  if (!Array.isArray(ranges) || ranges.length === 0) return { unreachable: [], gaps: [] };

  const reached = new Set<number>();
  const uncovered: number[] = [];
  for (let m = 0; m < MINUTES_IN_DAY; m++) {
    const i = firstMatchIndex(ranges, m);
    if (i == null) uncovered.push(m);
    else reached.add(i);
  }

  const unreachable = ranges
    .map((r, i) => ({ r, i }))
    .filter(
      ({ r, i }) =>
        timeToMinutes(r.startTime) != null &&
        timeToMinutes(r.endTime) != null &&
        typeof r.cutoffHours === 'number' &&
        !reached.has(i)
    )
    .map(({ i }) => i);

  // [startInclusive, endExclusive], matching how the ranges themselves read.
  const spans: Array<[number, number]> = [];
  let runStart: number | null = null;
  let prev: number | null = null;
  for (const m of uncovered) {
    if (runStart == null) runStart = m;
    else if (prev != null && m !== prev + 1) {
      spans.push([runStart, prev + 1]);
      runStart = m;
    }
    prev = m;
  }
  if (runStart != null && prev != null) spans.push([runStart, prev + 1]);

  // A hole crossing midnight surfaces as two spans. Report it as the single
  // wrap it is, so the fix — end the last range where the first begins — reads
  // straight off the message.
  if (spans.length > 1 && spans[0][0] === 0 && spans[spans.length - 1][1] === MINUTES_IN_DAY) {
    const head = spans.shift()!;
    const tail = spans.pop()!;
    spans.push([tail[0], head[1]]);
  }

  return {
    unreachable,
    gaps: spans.map(([from, to]) => `${minutesToTime(from)}–${minutesToTime(to)}`),
  };
}
